import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation';
import type { ForYouConfig } from '@atlassian/global-side-navigation/types';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useShouldRenderOverviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/controllers/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import {
	USER_NAVIGATION_CONFIGURATION_DEFAULT,
	USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { RECENT_QUERY_LIMIT } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';
import {
	FAVOURITE_QUERY_LIMIT,
	VALID_FAVORITE_TYPES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/common/constants.tsx';
import { useFilterMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/common/controllers/use-filter-menu-items/index.tsx';

import { getForYouConfig } from '../common/ui/for-you/getForYouConfig.tsx';
import { useShortcuts } from '../controllers/use-shortcuts/index.tsx';
import { useItemsConfig } from '../controllers/use-items-config/index.tsx';
import type { GSNForContentSidebar$key } from './__generated__/GSNForContentSidebar.graphql.ts';

type Props = {
	isShortcutsStatic?: boolean;
	queryReference: GSNForContentSidebar$key;
	customizationStore?: React.ComponentProps<typeof GlobalSideNavigation>['customizationStore'];
	networkStorageParams?: React.ComponentProps<typeof GlobalSideNavigation>['networkStorageParams'];
};

export function GSNForContentSidebar(props: Props): React.JSX.Element | null {
	const {
		isShortcutsStatic = false,
		queryReference,
		customizationStore,
		networkStorageParams,
	} = props;
	const data = useFragment(
		// Need to disable this rule for fragments that are used by child components
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		graphql`
			fragment GSNForContentSidebar on Query {
				...useNetworkSideNavUserData
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Overviews
				...Nav4Plans
				...Nav4Projects
			}
		`,
		queryReference,
	);

	const shouldRenderOverviews = useShouldRenderOverviews();
	const cloudId = useCloudId();
	const { isSelectedPath } = useSidebarNav4();

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		expVal('navx-1982-change-order-of-teams-and-goals', 'isEnabled', false)
			? USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED.navItems
			: USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
	);

	const items = useItemsConfig(navItems, data, shouldRenderOverviews);
	const recentEntryPointParams = useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]);
	const starredEntryPointParams = useMemo(
		() => ({
			cloudId,
			filter: { types: VALID_FAVORITE_TYPES, sort: { order: 'DESC' as const } },
			count: FAVOURITE_QUERY_LIMIT,
		}),
		[cloudId],
	);

	const { formatMessage } = useIntl();
	const starredFilterMenuItemDataRaw = useFilterMenuItems();
	const starredFilterConfig = useMemo(
		() =>
			starredFilterMenuItemDataRaw.map((item) => ({
				key: item.filterType,
				label: formatMessage(item.message),
				elemBefore: <item.Icon />,
			})),
		[formatMessage, starredFilterMenuItemDataRaw],
	);

	const forYouConfig = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			getForYouConfig({
				recentEntryPointParams,
				starredEntryPointParams,
				starredFilterConfig,
			}) as ForYouConfig<ObjectListFlyoutItem>,
		[recentEntryPointParams, starredEntryPointParams, starredFilterConfig],
	);

	// Determine peekMenuId from selectedPath
	const peekMenuId = navItems.find((item) => isSelectedPath(item.menuId))?.menuId;

	const shortcuts = useShortcuts();

	return (
		<GlobalSideNavigation
			app="jira"
			appLabel="Jira"
			customizationStore={customizationStore}
			forYou={forYouConfig}
			items={items}
			networkStorageParams={networkStorageParams}
			peekMenuId={peekMenuId}
			shortcuts={shortcuts}
			isShortcutsStatic={isShortcutsStatic} // Needed for VR tests as the URL can't be mocked
			testId="nav-platform-integration-sidebar.ui.global-side-navigation"
		/>
	);
}
